/**
 * User Profiles — Multi-user support with per-user settings.
 * Each user has their own Jira/Confluence configuration.
 * Stored in localStorage, keyed by email.
 */

export interface UserProfile {
  id: string
  name: string
  email: string
  role: string
  initials: string
  avatar?: string
  // Integration configs per user
  jira: {
    baseUrl: string
    email: string
    apiKey: string
    projectKey: string
  }
  confluence: {
    baseUrl: string
    email: string
    apiKey: string
    spaceKey: string
  }
  // MCP Cognito credentials (separate from Atlassian API tokens)
  // These authenticate against the Cognito pools that protect the MCP API Gateways
  mcpCredentials?: {
    jira: {
      username: string
      password: string
    }
    confluence: {
      username: string
      password: string
    }
  }
  // GitHub config per user
  github?: {
    org: string
    token?: string
  }
  // Brand overrides (optional)
  brand?: {
    name?: string
    company?: string
    logoInitial?: string
    jiraBaseUrl?: string
  }
  createdAt: string
  lastLogin: string
}
const PROFILES_KEY = 'vsm_user_profiles'
const ACTIVE_USER_KEY = 'vsm_active_user'

// Default users
const DEFAULT_PROFILES: UserProfile[] = [
  {
    id: 'admin',
    name: 'Admin User',
    email: 'admin@acme.com',
    role: 'Administrator',
    initials: 'AU',
    jira: {
      baseUrl: 'https://acme-corp.atlassian.net',
      email: 'develop@agent.ai',
      apiKey: 'ATATT3xFfGF0_mock_api_key_here',
      projectKey: 'VSM',
    },
    confluence: {
      baseUrl: 'https://acme-corp.atlassian.net/wiki',
      email: 'develop@agent.ai',
      apiKey: 'ATATT3xFfGF0_mock_api_key_here',
      spaceKey: 'VSM',
    },
    // MCP Cognito credentials for the admin's MCP backend
    mcpCredentials: {
      jira: {
        username: 'test_barbus@workium.ia',
        password: 'McpJira2026#',
      },
      confluence: {
        username: 'test_barbus@workium.ia',
        password: 'McpJira2026#',
      },
    },
    github: {
      org: 'workiumai',
    },
    brand: {
      name: 'VSM',
      company: 'ACME Corp',
      logoInitial: 'V',
      jiraBaseUrl: 'https://acme-corp.atlassian.net/browse',
    },
    createdAt: '2024-01-01',
    lastLogin: new Date().toISOString(),
  },
  {
    id: 'hdi-luis',
    name: 'Luis Gomez',
    email: 'luis.gomez@hdi.com.ar',
    role: 'Scrum Master',
    initials: 'LG',
    jira: {
      baseUrl: 'https://hdisegurossa.atlassian.net',
      email: 'luis.gomez@hdi.com.ar',
      apiKey:
        'ATATT3xFfGF0g53AVRYZgOHyY5nsWxN-oP_s7A9NuY63e4rYIxlc062mP0AUO5OhnS4mqDBnoXTiDs--7ZSpOw38yBcZd0bgKZWgs1Cg-cLCEjCTyEvd1AalZBXz_3BA6sKPOlHXg4Hy-RK9_1WYKjtCzSVEaQUYByExJZnFwFqcS8m-zWnCKE=226C2FB4',
      projectKey: '',
    },
    confluence: {
      baseUrl: 'https://hdisegurossa.atlassian.net/wiki',
      email: 'luis.gomez@hdi.com.ar',
      apiKey:
        'ATATT3xFfGF0g53AVRYZgOHyY5nsWxN-oP_s7A9NuY63e4rYIxlc062mP0AUO5OhnS4mqDBnoXTiDs--7ZSpOw38yBcZd0bgKZWgs1Cg-cLCEjCTyEvd1AalZBXz_3BA6sKPOlHXg4Hy-RK9_1WYKjtCzSVEaQUYByExJZnFwFqcS8m-zWnCKE=226C2FB4',
      spaceKey: '',
    },
    // Barbuss Risk needs their own MCP Cognito credentials
    // Without these, the system falls back to mock mode
    // To get these: create a Cognito user in the HDI MCP pools
    mcpCredentials: undefined,
    // NOT CONFIGURED — will force mock mode
    github: {
      org: 'hdi-seguros-argentina',
    },
    brand: {
      name: 'Barbuss Risk',
      company: 'Barbuss Risk',
      logoInitial: 'B',
      jiraBaseUrl: 'https://hdisegurossa.atlassian.net/browse',
    },
    createdAt: '2024-03-01',
    lastLogin: '',
  },
  {
    id: 'workium-test',
    name: 'Test Barbus',
    email: 'test_barbus@workium.ia',
    role: 'Developer',
    initials: 'TB',
    jira: {
      baseUrl: 'https://agentvsm.atlassian.net',
      email: 'test_barbus@workium.ia',
      apiKey: '',
      projectKey: 'VIVILO',
    },
    confluence: {
      baseUrl: 'https://agentvsm.atlassian.net/wiki',
      email: 'test_barbus@workium.ia',
      apiKey: '',
      spaceKey: '',
    },
    mcpCredentials: {
      jira: {
        username: 'test_barbus@workium.ia',
        password: 'McpJira2026#',
      },
      confluence: {
        username: 'test_barbus@workium.ia',
        password: 'McpJira2026#',
      },
    },
    github: {
      org: 'workiumai',
    },
    brand: {
      name: 'Workium AI',
      company: 'Workium AI',
      logoInitial: 'W',
      jiraBaseUrl: 'https://agentvsm.atlassian.net/browse',
    },
    createdAt: '2026-03-12',
    lastLogin: '',
  },
]

// Simple password store (in production, use proper auth)
const USER_PASSWORDS: Record<string, string> = {
  'admin@acme.com': 'admin123',
  'luis.gomez@hdi.com.ar': 'hdi2024',
  'test_barbus@workium.ia': 'McpJira2026#',
  'testuser@vsm-mcp-jira.dev': 'McpJira2026#',
}
function getProfiles(): UserProfile[] {
  try {
    const stored = localStorage.getItem(PROFILES_KEY)
    if (stored) {
      const profiles = JSON.parse(stored)
      // Merge with defaults to ensure new users are added
      const existingEmails = new Set(profiles.map((p: UserProfile) => p.email))
      for (const def of DEFAULT_PROFILES) {
        if (!existingEmails.has(def.email)) {
          profiles.push(def)
        }
      }
      return profiles
    }
  } catch {}
  return [...DEFAULT_PROFILES]
}
function saveProfiles(profiles: UserProfile[]) {
  try {
    localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles))
  } catch {}
}
export const userProfiles = {
  /** Authenticate a user by email + password (legacy local check) */
  authenticate(email: string, password: string): UserProfile | null {
    const normalizedEmail = email.toLowerCase().trim()
    const expectedPassword = USER_PASSWORDS[normalizedEmail]
    if (!expectedPassword || password !== expectedPassword) {
      return null
    }
    const profiles = getProfiles()
    const profile = profiles.find(
      (p) => p.email.toLowerCase() === normalizedEmail,
    )
    if (profile) {
      // Update last login
      profile.lastLogin = new Date().toISOString()
      saveProfiles(profiles)

      // Set as active user
      try {
        localStorage.setItem(ACTIVE_USER_KEY, normalizedEmail)
      } catch {}
      return profile
    }
    return null
  },
  /** Load a user profile by email (after Cognito auth — no password check) */
  authenticateWithoutPassword(email: string): UserProfile | null {
    const normalizedEmail = email.toLowerCase().trim()
    const profiles = getProfiles()
    const profile = profiles.find(
      (p) => p.email.toLowerCase() === normalizedEmail,
    )
    if (profile) {
      // Update last login
      profile.lastLogin = new Date().toISOString()
      saveProfiles(profiles)

      // Set as active user
      try {
        localStorage.setItem(ACTIVE_USER_KEY, normalizedEmail)
      } catch {}

      // Apply user's GitHub config (org + token)
      try {
        const { applyUserGitHubConfig } = require('./githubData')
        applyUserGitHubConfig(profile.github)
      } catch (err) {
        console.warn('[UserProfiles] Failed to apply GitHub config:', err)
      }
      return profile
    }
    return null
  },
  /** Get the currently active user */
  getActiveUser(): UserProfile | null {
    try {
      const email = localStorage.getItem(ACTIVE_USER_KEY)
      if (!email) return null
      const profiles = getProfiles()
      return (
        profiles.find((p) => p.email.toLowerCase() === email.toLowerCase()) ||
        null
      )
    } catch {
      return null
    }
  },
  /** Log out the current user */
  logout() {
    try {
      localStorage.removeItem(ACTIVE_USER_KEY)
    } catch {}
  },
  /** Check if a user is logged in */
  isLoggedIn(): boolean {
    return this.getActiveUser() !== null
  },
  /** Get all profiles */
  getAllProfiles(): UserProfile[] {
    return getProfiles()
  },
  /** Update a user's profile (settings) */
  updateProfile(email: string, updates: Partial<UserProfile>) {
    const profiles = getProfiles()
    const idx = profiles.findIndex(
      (p) => p.email.toLowerCase() === email.toLowerCase(),
    )
    if (idx >= 0) {
      profiles[idx] = {
        ...profiles[idx],
        ...updates,
      }
      saveProfiles(profiles)
      return profiles[idx]
    }
    return null
  },
  /** Update a user's Jira config */
  updateJiraConfig(email: string, jiraConfig: Partial<UserProfile['jira']>) {
    const profiles = getProfiles()
    const idx = profiles.findIndex(
      (p) => p.email.toLowerCase() === email.toLowerCase(),
    )
    if (idx >= 0) {
      profiles[idx].jira = {
        ...profiles[idx].jira,
        ...jiraConfig,
      }
      saveProfiles(profiles)
      return profiles[idx]
    }
    return null
  },
  /** Update a user's Confluence config */
  updateConfluenceConfig(
    email: string,
    confConfig: Partial<UserProfile['confluence']>,
  ) {
    const profiles = getProfiles()
    const idx = profiles.findIndex(
      (p) => p.email.toLowerCase() === email.toLowerCase(),
    )
    if (idx >= 0) {
      profiles[idx].confluence = {
        ...profiles[idx].confluence,
        ...confConfig,
      }
      saveProfiles(profiles)
      return profiles[idx]
    }
    return null
  },
  /** Create a new user profile */
  createProfile(
    profile: Omit<UserProfile, 'createdAt' | 'lastLogin'>,
    password: string,
  ): UserProfile {
    const profiles = getProfiles()
    const newProfile: UserProfile = {
      ...profile,
      createdAt: new Date().toISOString(),
      lastLogin: '',
    }
    profiles.push(newProfile)
    saveProfiles(profiles)

    // Save password
    USER_PASSWORDS[profile.email.toLowerCase()] = password
    return newProfile
  },
}
